import {
  InterventionGroupDrawer,
  type EnrichedPatient
} from '../components/InterventionGroupDrawer/InterventionGroupDrawer';
import AddTreatmentPlanModal from '../components/AddTreatmentPlanModal/AddTreatmentPlanModal';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useScenarioLock } from '../hooks/useScenarioLock';
import { useFetchPermissions } from '../hooks/usePermissions';
import { GanttChart } from '../components/GanttChart/GanttChart';
import { RecalculateWeekModal } from '../components/GanttChart/WeekKpiCell/RecalculateWeekModal';
import { useYearPlan } from '../hooks/useYearPlan';
import { useWeekCalculation } from '../hooks/useWeekCalculation';
import { useWeekCalculationStatuses } from '../hooks/useWeekCalculationStatuses';
import { useWeekCalendarModal } from '../hooks/useWeekCalendarModal';
import { IndividualCalendarModal } from '../components/WeekOverview/IndividualCalendarModal/IndividualCalendarModal';
import { Modal } from '../components/UI';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { showError } from '../store/errorDialog/errorDialogSlice';
import { selectPatientsById } from '../store/patients/patientsSlice';
import styles from './YearOverviewPage.module.css';
import { YearPicker } from 'qampo-ui-library';
import { formatDate } from '../utils/dateUtils';

export const YearOverviewPage = () => {
  const { t, i18n } = useTranslation();
  const { locationId } = useParams<{ locationId: string }>();
  const [selectedTreatmentPlanId, setSelectedTreatmentPlanId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [addTreatmentPlanBatchDialog, setAddTreatmentPlanBatchDialog] = useState<{
    treatmentPlanTemplateId: string;
    weekNumber: number;
    mode: 'single' | 'series';
  } | null>(null);
  const [addPatientOnOpen, setAddPatientOnOpen] = useState(false);

  const dispatch = useAppDispatch();
  const patientsById = useAppSelector(selectPatientsById);

  const {
    yearPlan,
    treatmentPlanTemplates,
    selectedYear,
    setSelectedYear,
    scenarioId,
    locationName,
    loading,
    changeSummary,
    handleAddTreatmentPlan,
    handleDeleteTreatmentPlan,
    updateCapacity,
    handleMoveIntervention,
    handleMoveStart,
    handleMoveDiscard,
    handleMoveAccept,
    handleDiscardAll,
    handleAcceptAll,
    refreshYearPlan,
    fetchTemplates,
    clearTemplates
  } = useYearPlan(locationId);

  useFetchPermissions(locationId);
  const { isLocked, lockedByCurrentUser, lockedByName, acquireLock, releaseLock } = useScenarioLock(
    locationId,
    scenarioId ?? undefined
  );
  const {
    statuses: weekCalculations,
    markWeekCalculating,
    refresh: retryCalculationStatuses
  } = useWeekCalculationStatuses(locationId, scenarioId ?? undefined);
  const {
    pendingWeekNumber,
    isTriggering,
    requestCalculation,
    cancelCalculation,
    confirmCalculation
  } = useWeekCalculation(locationId, scenarioId ?? undefined, markWeekCalculating);
  const {
    weekData: calendarWeekData,
    isLoading: isCalendarLoading,
    filters: calendarFilters,
    setFilters: setCalendarFilters,
    openCalendar,
    closeCalendar
  } = useWeekCalendarModal(locationId, scenarioId ?? undefined);

  const handleOpenCalendar = useCallback(
    (weekNumber: number) => {
      openCalendar(selectedYear, weekNumber);
    },
    [openCalendar, selectedYear]
  );

  const acquireLockAndRefresh = useCallback(async (): Promise<boolean> => {
    const acquired = await acquireLock();
    if (acquired) {
      await refreshYearPlan();
      await fetchTemplates();
    }
    return acquired;
  }, [acquireLock, refreshYearPlan, fetchTemplates]);

  const handleAddTreatmentPlanBatch = useCallback(
    (treatmentPlanTemplateId: string, weekNumber: number, mode: 'single' | 'series') => {
      setAddTreatmentPlanBatchDialog({ treatmentPlanTemplateId, weekNumber, mode });
    },
    []
  );

  const handleCloseBatchDialog = useCallback(() => {
    setAddTreatmentPlanBatchDialog(null);
  }, []);

  const handleConfirmBatchDialog = useCallback(
    (
      startWeekNumber: number,
      capacity: number,
      frequency: number,
      occurrences: number,
      endWeekNumber: number
    ) => {
      if (!addTreatmentPlanBatchDialog) return;
      const template = treatmentPlanTemplates.find(
        (t) => t.id === addTreatmentPlanBatchDialog.treatmentPlanTemplateId
      );
      if (template) {
        handleAddTreatmentPlan(
          template,
          startWeekNumber,
          capacity,
          frequency,
          occurrences,
          endWeekNumber
        );
      }
      setAddTreatmentPlanBatchDialog(null);
    },
    [addTreatmentPlanBatchDialog, treatmentPlanTemplates, handleAddTreatmentPlan]
  );

  const handleClose = useCallback(() => {
    setSelectedTreatmentPlanId(null);
    setSelectedGroupId(null);
  }, []);

  const handleGroupClick = useCallback((treatmentPlanId: string, groupId: string) => {
    setSelectedTreatmentPlanId(treatmentPlanId);
    setSelectedGroupId(groupId);
  }, []);

  const handleAddPatient = useCallback((treatmentPlanId: string, groupId: string) => {
    setSelectedTreatmentPlanId(treatmentPlanId);
    setSelectedGroupId(groupId);
    setAddPatientOnOpen(true);
  }, []);

  const handleDiscardAllAndRelease = useCallback(async () => {
    await handleDiscardAll();
    await releaseLock();
    clearTemplates();
  }, [handleDiscardAll, releaseLock, clearTemplates]);

  const handleDeleteAndCloseDrawer = useCallback(
    (treatmentPlanId: string) => {
      handleDeleteTreatmentPlan(treatmentPlanId);
      if (selectedTreatmentPlanId === treatmentPlanId) {
        setSelectedTreatmentPlanId(null);
        setSelectedGroupId(null);
      }
    },
    [handleDeleteTreatmentPlan, selectedTreatmentPlanId]
  );

  const handleAcceptAllAndRelease = useCallback(async () => {
    try {
      await handleAcceptAll();
      await releaseLock();
      clearTemplates();
    } catch (e) {
      dispatch(showError({ message: t('error-save-treatment-plans') }));
      throw e;
    }
  }, [handleAcceptAll, releaseLock, clearTemplates, t, dispatch]);

  const selectedTreatmentPlan = useMemo(
    () => yearPlan?.treatmentPlans.find((p) => p.id === selectedTreatmentPlanId),
    [yearPlan, selectedTreatmentPlanId]
  );

  const selectedGroup = useMemo(() => {
    if (!selectedTreatmentPlan || !selectedGroupId) return undefined;
    for (const week of selectedTreatmentPlan.weeks) {
      const group = (week.groups ?? []).find((g) => g.id === selectedGroupId);
      if (group) return group;
    }
    return undefined;
  }, [selectedTreatmentPlan, selectedGroupId]);

  const selectedPatients = useMemo<EnrichedPatient[]>(() => {
    if (!selectedGroup) return [];
    return (selectedGroup.assignedPatients ?? []).map((p) => ({
      ...p,
      name: patientsById[p.patientId]?.name
    }));
  }, [selectedGroup, patientsById]);

  const ganttChart = yearPlan ? (
    <GanttChart
      yearPlan={yearPlan}
      year={selectedYear}
      changeSummary={changeSummary}
      treatmentPlanTemplates={treatmentPlanTemplates}
      isLocked={isLocked}
      lockedByCurrentUser={lockedByCurrentUser}
      lockedByName={lockedByName}
      onAcquireLock={acquireLockAndRefresh}
      onGroupClick={handleGroupClick}
      onAddTreatmentPlan={handleAddTreatmentPlan}
      onMoveIntervention={handleMoveIntervention}
      onMoveStart={handleMoveStart}
      onMoveDiscard={handleMoveDiscard}
      onMoveAccept={handleMoveAccept}
      onDiscardAll={handleDiscardAllAndRelease}
      onAcceptAll={handleAcceptAllAndRelease}
      onAddTreatmentPlanBatch={handleAddTreatmentPlanBatch}
      onDeleteTreatmentPlan={handleDeleteAndCloseDrawer}
      onAddPatient={handleAddPatient}
      onWeekStatusClick={requestCalculation}
      onOpenCalendar={handleOpenCalendar}
      onRetryCalculationStatuses={retryCalculationStatuses}
      weekCalculations={weekCalculations}
    />
  ) : null;

  return (
    <main className={styles.yearOverview}>
      <h1 className={styles.heading}>
        {t('nav-year-overview')}{' '}
        <div className={styles.yearPicker}>
          <YearPicker
            value={selectedYear}
            onChange={lockedByCurrentUser ? () => {} : setSelectedYear}
            styles={lockedByCurrentUser ? { root: { pointerEvents: 'none', opacity: 0.6 } } : undefined}
            theme={{
              inputBackground: 'transparent',
              popupBackground: 'var(--color-surface-primary)',
              yearSelectedBackground: 'var(--color-interaction-primary)',
              yearSelectedBorderRadius: '15px'
            }}
          />
        </div>
      </h1>
      {locationName && (
        <div className={styles.subHeading}>
          <p className={styles.locationName}>{locationName}</p>
          {yearPlan?.lastModifiedDate && (
            <p className={styles.lastModified}>
              {t('last-modified')} {formatDate(yearPlan.lastModifiedDate, i18n.language)}
            </p>
          )}
        </div>
      )}
      {loading ? <p>Loading...</p> : ganttChart}

      {/* Loading shell: instant feedback on the header click, and it blocks
          the page so no second modal can open under the settling calendar */}
      {isCalendarLoading && (
        <Modal title={t('week-overview-modal-title')} onClose={closeCalendar}>
          <p>{t('week-overview-calendar-loading')}</p>
        </Modal>
      )}

      {calendarWeekData && (
        <IndividualCalendarModal
          weekData={calendarWeekData}
          filters={calendarFilters}
          onFiltersChange={setCalendarFilters}
          onClose={closeCalendar}
        />
      )}

      {pendingWeekNumber !== null && (
        <RecalculateWeekModal
          weekNumber={pendingWeekNumber}
          isTriggering={isTriggering}
          onConfirm={confirmCalculation}
          onClose={cancelCalculation}
        />
      )}

      <AddTreatmentPlanModal
        treatmentPlanTemplateId={addTreatmentPlanBatchDialog?.treatmentPlanTemplateId ?? ''}
        weekNumber={addTreatmentPlanBatchDialog?.weekNumber ?? 1}
        mode={addTreatmentPlanBatchDialog?.mode ?? 'single'}
        weeks={yearPlan?.weeks ?? []}
        treatmentPlanTemplates={treatmentPlanTemplates}
        isOpen={addTreatmentPlanBatchDialog !== null}
        onClose={handleCloseBatchDialog}
        onConfirm={handleConfirmBatchDialog}
      />
      <InterventionGroupDrawer
        isOpen={selectedTreatmentPlanId !== null}
        onClose={handleClose}
        treatmentPlan={selectedTreatmentPlan}
        selectedGroup={selectedGroup}
        patients={selectedPatients}
        weeks={yearPlan?.weeks}
        isEditMode={lockedByCurrentUser}
        onCapacityChange={(capacity, groupIds) => {
          if (!selectedTreatmentPlanId) return;
          updateCapacity(selectedTreatmentPlanId, capacity, groupIds);
        }}
        onSelectGroup={setSelectedGroupId}
        patientsById={patientsById}
        addPatientOnOpen={addPatientOnOpen}
        onAddPatientHandled={() => setAddPatientOnOpen(false)}
        locationId={locationId}
        scenarioId={scenarioId ?? undefined}
        onPatientRemoved={() => void refreshYearPlan()}
        onPatientAdded={() => void refreshYearPlan()}
      />
    </main>
  );
};
